import * as React from 'react'

import { Alert, Box, Text } from '@factorialco/deprecated-design-system'
import compact from 'lodash/compact'
import includes from 'lodash/includes'
import sortBy from 'lodash/sortBy'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import type { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'
import type { SelectionMetadata } from 'modules/core/components/EntitySelectorV2/types'
import createComputed from 'modules/core/hooks/createComputed'
import { useCallback } from 'modules/core/hooks/useCallback'
import { useEffect } from 'modules/core/hooks/useEffect'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { getFullDisplayName } from 'modules/core/lib/userDisplayName'
import users from 'modules/core/store/collections/users'
import app from 'modules/core/store/models/app'

import EsignatureRequester from 'modules/documents/components/BulkFiles/DocumentAssigner/EsignatureRequester'
import AssignConfirmationModal from 'modules/documents/components/BulkFiles/DocumentAssigner/MultiDocumentAssignerForm/AssignConfirmationModal'
import { Document as DocumentEntity } from 'modules/documents/hooks/documents/useGetDocuments'
import { Document } from 'modules/documents/hooks/documents/useGetDocuments'
import { UploadProcess } from 'modules/documents/hooks/useGetUploadProcess'
import {
  UploadProcessDocument,
  useGetUploadProcessDocuments
} from 'modules/documents/hooks/useGetUploadProcessDocuments'
import { useSendInBulkUpdate } from 'modules/documents/hooks/useSendInBulkUpdate'
import { isTemplate } from 'modules/documents/lib/Documents'
import { ESIGNATURE_MAX_FILE_SIZE } from 'modules/documents/lib/constants'
import { isPersonalFilesWorkflow } from 'modules/documents/lib/distributionWorkflows'
import type { AssignEsignatureForm } from 'modules/documents/lib/forms/AssignEsignatureForm'
import scores from 'modules/documents/store/collections/scores'
import type Score from 'modules/documents/store/models/Score'
import type { Employees } from 'modules/employees/store/collections/employees'
import employees from 'modules/employees/store/collections/employees'
import type Employee from 'modules/employees/store/models/Employee'

const SUGGESTED_SCORE_THRESHOLD = 10
const SELECTED_SCORE_THRESHOLD = 100
const MAX_DEFAULT_MATCHES = 1

type EsignatureRequesterProps = React.ComponentProps<typeof EsignatureRequester>

const assigningLabelFromEmployees = (selectedEmployees: Employee[]) => {
  if (!selectedEmployees || selectedEmployees.length === 0) {
    return i18n.t('split_documents.assign_documents.select_employees')
  }
  return i18n.tp('split_documents.assign_documents.send_to', {
    count: selectedEmployees.length,
    employee: selectedEmployees.map((e) => {
      const user = users.get(e.attributes.access_id)
      return user?.attributes?.first_name ?? ''
    })
  })
}

const assigningLabelFromCount = (count: number) => {
  if (count === 0) {
    return i18n.t('split_documents.assign_documents.select_employees')
  }
  return i18n.tp('split_documents.assign_documents.send_to', {
    count
  })
}

const getSelectedEmployees = createComputed((selectedIds: string[]) => {
  return compact(selectedIds.map((id) => employees.get(id)))
})

const sortEmployees = (employeesToSort: Employee[]) => {
  return sortBy(employeesToSort, (employee) => {
    const userId = employee.attributes.access_id
    const user = users.get(userId)
    return user
      ? getFullDisplayName({
          firstName: user.attributes.first_name,
          lastName: user.attributes.last_name,
          canSharePreferredName: employee.policy.can('sharePreferredName'),
          preferredName: employee.attributes.preferred_name
        })
      : ''
  })
}

const filterScores = (
  filteredEmployees: Employees,
  document: Document,
  scoreFn: (score: Score) => boolean
) => {
  const employeeIds = scores
    .filter((score) => String(score.attributes.document_id) === document.id)
    .filter(scoreFn)
    .map((score: Score) => String(score.attributes.employee_id))

  return filteredEmployees.filter((employee) =>
    includes(employeeIds, String(employee.attributes.id))
  )
}

const getConfirmedEmployees = (filteredEmployees: Employees, document: Document) => {
  const confirmedEmployees = filterScores(
    filteredEmployees,
    document,
    (score: Score) => score.attributes.score >= SELECTED_SCORE_THRESHOLD
  )

  return sortEmployees(confirmedEmployees)
}

const getSuggestedEmployees = (filteredEmployees: Employees, document: Document) => {
  const suggestedEmployees = filterScores(
    filteredEmployees,
    document,
    (score: Score) =>
      score.attributes.score >= SUGGESTED_SCORE_THRESHOLD &&
      score.attributes.score < SELECTED_SCORE_THRESHOLD
  )

  return sortEmployees(suggestedEmployees)
}

interface Props {
  document: Document
  selectedIdsForEsignature: string[]
  onSelectIdsForEsignature: (arg0: string[]) => void
  requestEsignatureForm: AssignEsignatureForm
  uploadProcess: UploadProcess
}

const MultiDocumentAssignerForm = ({
  document,
  selectedIdsForEsignature,
  requestEsignatureForm,
  onSelectIdsForEsignature,
  uploadProcess
}: Props) => {
  const [loadingScores, setLoadingScores] = React.useState(false)
  const [loadingEmployees, setLoadingEmployees] = React.useState(false)
  const [open, setOpen] = React.useState(false)
  const [selectedIds, setSelectedIds] = React.useState<string[]>([])
  const [confirmedEmployeeIds, setConfirmedEmployeeIds] = React.useState<string[] | undefined>(
    undefined
  )
  const [suggestedEmployeeIds, setSuggestedEmployeeIds] = React.useState<string[] | undefined>(
    undefined
  )
  const [employeeSelection, setEmployeeSelection] =
    React.useState<EmployeeSelectorV2MultiSelectState | null>(null)
  const [selectionCount, setSelectionCount] = React.useState(0)
  const [uploadProcessDocument, setUploadProcessDocument] = React.useState<
    UploadProcessDocument | undefined
  >()
  const selectedEmployees = getSelectedEmployees(selectedIds).get()
  const selectedSignees = getSelectedEmployees(selectedIdsForEsignature).get()

  const isSelectAll = employeeSelection?.type === 'all'
  const selectedCount = isSelectAll ? selectionCount : selectedIds.length

  const assignLabel = isSelectAll
    ? assigningLabelFromCount(selectedCount)
    : assigningLabelFromEmployees(selectedEmployees)

  const { loading: loadingUploadProcessDocuments, fetch: fetchUploadProcessDocuments } =
    useGetUploadProcessDocuments()

  const fetchCurrentUploadProcessDocument = useCallback(async () => {
    const currentUploadProcessDocument = await fetchUploadProcessDocuments({
      documentIds: [document.id]
    })
    return currentUploadProcessDocument?.[0]
  }, [document.id])

  const gatherUploadProcessDocuments = async () => {
    setUploadProcessDocument(await fetchCurrentUploadProcessDocument())
  }

  const { submit: onSubmitSendInBulk } = useSendInBulkUpdate()

  useEffect(() => {
    const gatherScores = async () => {
      setLoadingScores(true)
      await scores.fetch({
        data: {
          document_ids: [document.id]
        }
      })

      setSelectedIds([])
      setEmployeeSelection(null)
      setSelectionCount(0)

      const confirmed = getConfirmedEmployees(employees, document)
      const suggested = getSuggestedEmployees(employees, document)
      setConfirmedEmployeeIds(confirmed.map((e) => e.attributes.id))
      setSuggestedEmployeeIds(suggested.map((e) => e.attributes.id))

      const matchIds = [...confirmed].map((employee) => employee.attributes.id)
      if (MAX_DEFAULT_MATCHES >= matchIds.length) {
        setSelectedIds(matchIds)
        if (matchIds.length > 0) {
          setEmployeeSelection({ type: 'manual', ids: matchIds })
          setSelectionCount(matchIds.length)
        }
      }
      setLoadingScores(false)
    }

    if (isPersonalFileWorkflow) {
      gatherScores()
    }
    gatherUploadProcessDocuments()
  }, [document.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const onAssignDocument = async () => {
    const requestEsignature = uploadProcess.esignatureRequest ?? false
    setLoadingEmployees(true)
    app.setProgress({
      message: i18n.t('common.sending')
    })

    if (requestEsignature && document.fileSize && document.fileSize > ESIGNATURE_MAX_FILE_SIZE) {
      setLoadingEmployees(false)
      app.setProgress(null)
      app.setFlash({
        type: 'wrong',
        message: i18n.t('documents.beta.file_errors.max_file_size_esignature')
      })
      return
    }

    if (!isSelectAll && (!selectedEmployees || selectedEmployees.length === 0)) {
      setLoadingEmployees(false)
      app.setProgress(null)
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_saving')
      })
      throw new Error("Can't assign document to null employee")
    }

    const esignatureRequest = uploadProcess.esignatureRequest ?? false
    const additionalSigners =
      (uploadProcessDocument?.additionalSigners &&
      uploadProcessDocument?.additionalSigners.length > 0
        ? uploadProcessDocument?.additionalSigners.map((signer) => signer.id)
        : uploadProcess?.additionalSigners.map((signer) => signer.id)) || []

    const employeeSelectionInput = isSelectAll
      ? {
          type: 'all' as const,
          filters: employeeSelection.filters,
          excludedIds: employeeSelection.excludedIds
        }
      : {
          type: 'manual' as const,
          ids: selectedEmployees.map((employee) => employee.attributes.id)
        }

    const result = await onSubmitSendInBulk({
      id: document.id,
      byPendingAssignment: true,
      distributionMethod: 'bulk',
      employeeIds: isSelectAll ? [] : selectedEmployees.map((employee) => employee.attributes.id),
      employeeSelection: employeeSelectionInput,
      isPendingAssignment: false,
      public: true,
      requestEsignature: esignatureRequest,
      signeeIds: additionalSigners
    })

    const handled = handleResult(result, () => undefined)

    const documentIds =
      handled.success && handled.data.documents?.map((doc: DocumentEntity) => doc.id)

    if (documentIds) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('split_documents.bulk_modal.loading.description')
      })
    }

    setLoadingEmployees(false)
    app.setProgress(null)
  }

  const onConfirm = () => {
    setOpen(false)
    onAssignDocument()
  }

  const onClose = () => {
    setOpen(false)
  }

  const onSubmit: EsignatureRequesterProps['onClick'] = async () => {
    if (!isSelectAll && selectedIds.length <= 1 && !isTemplate(document)) {
      await onAssignDocument()
    } else {
      setOpen(true)
    }
  }

  const setAssignerId = (value: string | null | undefined) => {
    if (!value) return
    if (selectedIds.includes(value)) return setSelectedIds(selectedIds.filter((id) => id !== value))
    if (isPersonalFileWorkflow) return setSelectedIds([value])

    setSelectedIds([...selectedIds, value])
  }

  const isPersonalFileWorkflow = isPersonalFilesWorkflow(uploadProcess)
  const loading = loadingScores || loadingEmployees || loadingUploadProcessDocuments
  const isDisabled = (() => {
    if (loading) return true
    if (isSelectAll) return selectedCount === 0
    return !selectedIds || selectedIds.length === 0
  })()

  if (loading) return <Loading />

  return (
    <Box flexDirection="column" height="full">
      <Box flexGrow>
        <Box flexDirection="column" padding="s16" minHeight="s64" justifyContent="center">
          <Text.Normal weight="medium">
            {i18n.t('split_documents.assign_documents.recipients_description_v2')}
          </Text.Normal>
        </Box>
        <Box padding="s16" paddingTop="s0" data-testid="employee-selector-container">
          {isPersonalFileWorkflow ? (
            <EmployeeSelectorV2
              value={selectedIds[0]}
              onChange={(id: string | undefined) => {
                setSelectedIds(id != null ? [id] : [])
                setEmployeeSelection(id != null ? { type: 'manual', ids: [id] } : null)
                setSelectionCount(id != null ? 1 : 0)
              }}
              forcedFilters={{
                confirmedEmployeeIds,
                suggestedEmployeeIds
              }}
              options={{ disableSelectAll: true, asList: true }}
            />
          ) : (
            <EmployeeSelectorV2
              multiple
              value={selectedIds}
              onChange={(
                state: EmployeeSelectorV2MultiSelectState,
                metadata: SelectionMetadata
              ) => {
                setEmployeeSelection(state)
                setSelectionCount(metadata.selectedCount)
                if (state.type === 'manual') {
                  setSelectedIds(state.ids)
                } else {
                  // When type is 'all', clear selectedIds since we use employeeSelection directly
                  setSelectedIds([])
                }
              }}
              options={{ asList: true }}
            />
          )}
        </Box>
      </Box>
      <Box
        padding="s16"
        gap="s16"
        background="grey200"
        border={{
          top: {
            color: 'grey400',
            width: 's1',
            style: 'solid'
          }
        }}
        style={{ bottom: '0', position: 'sticky' }}
      >
        {isPersonalFileWorkflow && (
          <Box gap="s16">
            <Alert color="warning">
              <Text.Normal color="grey1000">
                {i18n.t('split_documents.assign_documents.personal_files_alert')}
              </Text.Normal>
            </Alert>
          </Box>
        )}
        <EsignatureRequester
          selectedIdsForEsignature={selectedIdsForEsignature}
          recipientsCount={selectedCount}
          requestEsignatureForm={requestEsignatureForm}
          onSelectIdsForEsignature={onSelectIdsForEsignature}
          uploadProcess={uploadProcess}
          uploadProcessDocument={uploadProcessDocument}
          onSignersUpdateFinished={gatherUploadProcessDocuments}
          disabled={isDisabled}
          onClick={onSubmit}
          label={assignLabel}
        />
      </Box>
      {open && (
        <AssignConfirmationModal
          fileName={document.filename}
          employees={isSelectAll ? [] : selectedEmployees}
          employeeCount={isSelectAll ? selectedCount : undefined}
          requestEsignature={requestEsignatureForm.get('requestEsignature').value}
          signees={selectedSignees}
          onClose={onClose}
          onConfirm={onConfirm}
          document={document}
        />
      )}
    </Box>
  )
}

export default observer(MultiDocumentAssignerForm)
